import { useState, useEffect, useCallback } from 'react';
import { crearCampania, listarCampanias, eliminarCampania, subirPDFCampania } from '../services/campanias';
import { getContadores } from '../services/usuario';
import '../styles/Campanias.css';

export default function Campanias() {
  const [campanias, setCampanias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [nuevaCampaniaNombre, setNuevaCampaniaNombre] = useState('');
  const [archivoPDF, setArchivoPDF] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  // 🔥 Estados para contadores de limitaciones (actualizado a 50)
  const [contadores, setContadores] = useState({
    pdfs_almacenados: 0,
    procesamientos_hoy: 0,
    limite_pdfs_almacenados: 50,
    limite_procesamientos_diarios: 50
  });

  const cargarCampanias = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCampanias();
      setCampanias(data);
      
      // 🔥 Cargar contadores de limitaciones
      try {
        const contadoresData = await getContadores();
        setContadores(contadoresData);
      } catch (err) {
        console.error('Error cargando contadores:', err);
      }
      
    } catch (error) {
      console.error('Error cargando campañas:', error);
      setError('Error al cargar las campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarCampanias();
  }, [cargarCampanias]);

  const handleCrearCampaniaConPDF = async (e) => {
    e.preventDefault();
    if (!nuevaCampaniaNombre.trim()) {
      setError('El nombre de la campaña es obligatorio');
      return;
    }
    if (!archivoPDF) {
      setError('Debes seleccionar un archivo PDF');
      return;
    }
    setUploading(true);
    setError('');
    try {
      // 1. Crear la campaña
      const nueva = await crearCampania({ nombre: nuevaCampaniaNombre });
      
      // 2. Subir y procesar el PDF
      const resultado = await subirPDFCampania(nueva.id, archivoPDF);
      
      alert(`✅ Campaña "${nueva.nombre}" creada.\n${resultado.mensaje}\nPáginas: ${resultado.paginas}\nChunks generados: ${resultado.total_chunks}\nCréditos restantes: ${resultado.creditos_restantes}\nPDFs almacenados: ${resultado.pdfs_almacenados}/50`);
      
      setShowModal(false);
      setNuevaCampaniaNombre('');
      setArchivoPDF(null);
      cargarCampanias();
    } catch (error) {
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña? Se perderán todos los chunks y contenido asociado.')) return;
    try {
      await eliminarCampania(id);
      setCampanias(campanias.filter(c => c.id !== id));
      // 🔥 Recargar contadores después de eliminar
      try {
        const contadoresData = await getContadores();
        setContadores(contadoresData);
      } catch (err) {
        console.error('Error recargando contadores:', err);
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case 'completado':
        return <span className="estado-badge completado">✅ Completado</span>;
      default:
        return <span className="estado-badge pendiente">⏳ Pendiente</span>;
    }
  };

  // 🔥 Calcular si el usuario ha alcanzado el límite de PDFs almacenados
  const haAlcanzadoLimitePDFs = contadores.pdfs_almacenados >= contadores.limite_pdfs_almacenados;
  const haAlcanzadoLimiteProcesamientos = contadores.procesamientos_hoy >= contadores.limite_procesamientos_diarios;
  const porcentajePDFs = (contadores.pdfs_almacenados / contadores.limite_pdfs_almacenados) * 100;
  const porcentajeProcesamientos = (contadores.procesamientos_hoy / contadores.limite_procesamientos_diarios) * 100;

  return (
    <div className="campanias-container">
      <div className="campanias-header">
        <h1>🚀 Campañas de Marketing</h1>
        <button 
          className="btn-crear-campania" 
          onClick={() => setShowModal(true)}
          disabled={haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos}
          title={haAlcanzadoLimitePDFs ? 'Has alcanzado el límite de 50 PDFs almacenados. Elimina uno para subir otro.' : haAlcanzadoLimiteProcesamientos ? 'Has alcanzado el límite de 50 procesamientos en el día. Vuelve mañana.' : ''}
        >
          + Nueva Campaña {(haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos) && '🔒'}
        </button>
      </div>

      {/* 🔥 INDICADORES DE LÍMITES (actualizado a 50) */}
      <div className="limites-container">
        <div className="limite-card">
          <div className="limite-header">
            <span className="limite-label">📄 PDFs Almacenados</span>
            <span className="limite-valor">{contadores.pdfs_almacenados} / {contadores.limite_pdfs_almacenados}</span>
          </div>
          <div className="limite-barra">
            <div 
              className={`limite-progreso ${porcentajePDFs >= 90 ? 'limite-peligro' : ''}`}
              style={{ width: `${Math.min(porcentajePDFs, 100)}%` }}
            ></div>
          </div>
          <small className="limite-hint">
            {haAlcanzadoLimitePDFs ? '⚠️ Límite alcanzado. Elimina un PDF para continuar.' : `Te quedan ${contadores.limite_pdfs_almacenados - contadores.pdfs_almacenados} espacios`}
          </small>
        </div>

        <div className="limite-card">
          <div className="limite-header">
            <span className="limite-label">⚡ Procesamientos Hoy</span>
            <span className="limite-valor">{contadores.procesamientos_hoy} / {contadores.limite_procesamientos_diarios}</span>
          </div>
          <div className="limite-barra">
            <div 
              className={`limite-progreso ${porcentajeProcesamientos >= 90 ? 'limite-peligro' : ''}`}
              style={{ width: `${Math.min(porcentajeProcesamientos, 100)}%` }}
            ></div>
          </div>
          <small className="limite-hint">
            {haAlcanzadoLimiteProcesamientos 
              ? '⚠️ Límite diario alcanzado. Vuelve mañana.' 
              : `Te quedan ${contadores.limite_procesamientos_diarios - contadores.procesamientos_hoy} procesamientos hoy`}
          </small>
        </div>
      </div>

      <div className="campanias-stats">
        <div className="stat-card">
          <span className="stat-valor">{campanias.length}</span>
          <span className="stat-label">Total campañas</span>
        </div>
        <div className="stat-card">
          <span className="stat-valor">{campanias.filter(c => c.estado === 'completado').length}</span>
          <span className="stat-label">Completadas</span>
        </div>
        <div className="stat-card">
          <span className="stat-valor">{campanias.filter(c => c.estado === 'pendiente').length}</span>
          <span className="stat-label">Pendientes</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">Cargando campañas...</div>
      ) : campanias.length === 0 ? (
        <div className="sin-campanias">
          <p>No tienes campañas aún. Crea tu primera campaña para empezar.</p>
          <button 
            className="btn-crear-primera" 
            onClick={() => setShowModal(true)}
            disabled={haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos}
          >
            {(haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos) ? 'Límite alcanzado' : '+ Crear primera campaña'}
          </button>
        </div>
      ) : (
        <div className="tabla-campanias-container">
          <table className="campanias-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>PDF</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {campanias.map((campania) => (
                <tr key={campania.id}>
                  <td className="nombre-col">{campania.nombre}</td>
                  <td className="pdf-col">
                    {campania.pdf_original_nombre ? (
                      <span className="pdf-nombre" title={campania.pdf_original_nombre}>
                        📄 {campania.pdf_original_nombre.length > 30 ? campania.pdf_original_nombre.substring(0, 30) + '...' : campania.pdf_original_nombre}
                      </span>
                    ) : (
                      <span className="sin-pdf">Sin PDF</span>
                    )}
                  </td>
                  <td>{new Date(campania.fecha_creacion).toLocaleDateString()}</td>
                  <td>{getEstadoBadge(campania.estado)}</td>
                  <td className="acciones-col">
                    <button className="btn-eliminar" onClick={() => handleEliminar(campania.id)}>
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL CREAR CAMPAÑA CON PDF */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nueva Campaña</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCrearCampaniaConPDF}>
              <div className="modal-field">
                <label>Nombre de la campaña *</label>
                <input
                  type="text"
                  value={nuevaCampaniaNombre}
                  onChange={(e) => setNuevaCampaniaNombre(e.target.value)}
                  required
                  placeholder="Ej: Lanzamiento Producto X"
                  autoFocus
                  disabled={uploading}
                />
              </div>
              <div className="modal-field">
                <label>Archivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setArchivoPDF(e.target.files[0])}
                  required
                  disabled={uploading || haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos}
                />
                <small>El PDF será procesado para extraer el contenido y generar embeddings.</small>
                {haAlcanzadoLimitePDFs && (
                  <small className="error-text">⚠️ Has alcanzado el límite de 50 PDFs almacenados. Elimina uno para subir otro.</small>
                )}
                {haAlcanzadoLimiteProcesamientos && !haAlcanzadoLimitePDFs && (
                  <small className="error-text">⚠️ Has alcanzado el límite de 50 procesamientos en el día. Vuelve mañana.</small>
                )}
              </div>
              {error && <div className="modal-error">{error}</div>}
              <button 
                type="submit" 
                className="modal-btn" 
                disabled={uploading || haAlcanzadoLimitePDFs || haAlcanzadoLimiteProcesamientos}
              >
                {uploading ? 'Creando y procesando...' : 'Crear campaña y subir PDF'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}